<script lang="ts">
  import { flattenDeep, groupBy } from 'lodash-es'
  import { useNavigate } from 'svelte-navigator'
  import { saveUserAnswers, userAnswers } from '../api/api'
  import { ROUTES } from '../constants/constants'
  import { userIdGenerator } from '../lib/utils'
  import { surveyStatus, currentQuestion, canGoNext, progress, progressCount } from '../stores'
  import type { Question } from '../types/question'
  import type { SurveyStatus } from '../types/survey'

  const navigate = useNavigate()

  const addCurrentAnswer = (surveyStatus: SurveyStatus) => {
    let { userAnswersList, currentUserAnswer } = surveyStatus

    // remove flag
    currentUserAnswer.forEach((answer) => {
      if (!answer.isOldAnswer) {
        return
      }
      answer.isOldAnswer = false
    })

    let flatUserAnswersList = flattenDeep(userAnswersList)

    if (
      flatUserAnswersList.some((answer) => currentUserAnswer[0].questionId === answer.questionId)
    ) {
      // save index of the first matching element
      const foundIndex = flatUserAnswersList.findIndex(
        (answer) => currentUserAnswer[0].questionId === answer.questionId
      )

      // remove matching elements
      flatUserAnswersList = flatUserAnswersList.filter(
        (answer) => currentUserAnswer[0].questionId !== answer.questionId
      )

      // group answers by questionId
      const grouped = groupBy(flatUserAnswersList, 'questionId')

      // turn back into array of arrays
      userAnswersList = Object.values(grouped)

      // insert new user answer in correct position
      userAnswersList.splice(foundIndex, 0, currentUserAnswer)
    } else {
      userAnswersList.push(currentUserAnswer)
    }

    console.log('userAnswersList: ', userAnswersList)

    return {
      ...surveyStatus,
      userAnswersList: userAnswersList,
    }
  }

  const updateStatus = (surveyStatus: SurveyStatus, currentQuestion: Question) => {
    const flatUserAnswersList = flattenDeep(surveyStatus.userAnswersList)

    const nextQuestionId = currentQuestion.nextQuestionId
      ? currentQuestion.nextQuestionId(surveyStatus.userAnswersList)
      : surveyStatus.currentUserAnswer[0].nextQuestionId(surveyStatus.userAnswersList)

    return {
      ...surveyStatus,
      currentUserAnswer:
        flatUserAnswersList.filter((answer) => answer.questionId === nextQuestionId) || [],
      currentQuestionId: nextQuestionId,
    }
  }

  const moveForward = (surveyStatus: SurveyStatus, currentQuestion: Question) => {
    surveyStatus = addCurrentAnswer(surveyStatus)
    $progress = $progress + 1
    $progressCount = $progressCount + 1
    return updateStatus(surveyStatus, currentQuestion)
  }

  const moveForwardAndSave = () => {
    if ($surveyStatus.currentUserAnswer.length === 0 && !$currentQuestion.isSkippable) return

    $surveyStatus = moveForward($surveyStatus, $currentQuestion)

    if ($surveyStatus.currentQuestionId === 'end_of_survey') {
      ;(async () => {
        const userId = userIdGenerator() // FIXME: better solution
        const flatUserAnswersList = flattenDeep($surveyStatus.userAnswersList)
        const userAnswersByQuestionId = groupBy(flatUserAnswersList, 'questionId')
        await saveUserAnswers(userId, userAnswersByQuestionId)
        navigate(`/${ROUTES.dataWall}/${userId}`)
      })()
    }
  }

  // export let showModal
</script>

<svelte:window
  on:keydown={(event) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault()
      moveForwardAndSave()
    }
  }}
/>

<button
  style={$canGoNext ? 'background-color: #00fff0;' : 'background-color: #2d2d2d'}
  class={$surveyStatus.currentQuestionId === 'start_survey'
    ? 'begin-survey'
    : $surveyStatus.currentQuestionId === 'end_of_survey'
    ? 'end-survey'
    : 'move-forward'}
  disabled={!$canGoNext}
  on:click={moveForwardAndSave}
>
  {#if $surveyStatus.currentQuestionId === 'start_survey'}
    <p>Partecipa al sondaggio</p>
    <span
      ><svg
        width="18"
        height="19"
        viewBox="0 0 18 19"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M17.475 2C17.475 1.26225 16.8769 0.664189 16.1392 0.664189L4.11689 0.664189C3.37914 0.664189 2.78108 1.26225 2.78108 2C2.78108 2.73775 3.37914 3.33581 4.11689 3.33581L14.8034 3.33581V14.0223C14.8034 14.76 15.4014 15.3581 16.1392 15.3581C16.8769 15.3581 17.475 14.76 17.475 14.0223V2ZM1.94456 18.0837L17.0837 2.94456L15.1946 1.05544L0.0554392 16.1946L1.94456 18.0837Z"
          fill="black"
        />
      </svg>
    </span>
  {:else if $surveyStatus.currentQuestionId === 'suggestions'}
    <svg width="26" height="20" viewBox="0 0 26 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M8.26265 15.7793L2.09892 9.56003L0 11.6629L8.26265 20L26 2.10291L23.9159 0L8.26265 15.7793Z"
        fill="#1F1F1F"
      />
    </svg>
  {:else}
    <svg width="26" height="19" viewBox="0 0 26 19" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M16.4278 0.666687L14.8956 2.2022L20.9593 8.28979H0.878906V10.4678H20.9593L14.8956 16.5554L16.4278 18.0909L25.1213 9.37881L16.4278 0.666687Z"
        fill="#4E4E4E"
      />
    </svg>
  {/if}
</button>

<style>
  button {
    cursor: pointer;
    max-height: 60px;
    display: flex;
    align-items: center;
    background: #2d2d2d;
    color: #4e4e4e;
    border: none;
    border-radius: 0;
    padding: 1.2rem 1.5rem;
    margin: 0;
  }

  button:disabled {
    cursor: initial;
  }

  button:active {
    background-color: transparent;
  }

  .begin-survey {
    color: black;
    font-size: 20px;
    font-weight: 700;
    background-color: #00fff0;
    width: 100vw;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .begin-survey span {
    position: absolute;
    bottom: 0;
    right: 0;
    padding: 1.2rem 1.5rem;
    display: flex;
    align-items: center;
  }

  .end-survey {
    color: #1f1f1f;
    background-color: #00fff0;
  }
  p {
    margin: 0;
  }
</style>
